/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useEffect, useState, useRef } from 'preact/hooks';
import { Transition } from 'react-transition-group';
import { useInView } from 'react-intersection-observer';
import { InteractionEvent, getAnalyticsContext } from '@playbook/sdk-helpers';

import styleSheet from './components/styles/scorePredictor.module.scss';

import Sponsor from './components/sponsor.component';

import ScheduledScreen from './scheduledScreen';

import ScorePredictorHeader from './components/scorePredictorHeader.component';
import ScorePredictorContent from './components/ScorePredictorContent.component';
import { getScorePredictorData } from './apiRequests';

// import PredictionContent from './components/predictionContent';
import {
    getAuthHeader,
    getUserIdFromAuthHeader
} from '../helpers/authentication';
import { sendView } from '../helpers/analytics';
import { getOrCreateEncodedUUID } from '../helpers/user-meta';

/**
 * Check is given score predictor is not open for voting yet
 *
 * @param {object} scorePredictor scorePredictor data
 * @returns {boolean} poll is scheduled
 */
export const isScheduled = (scorePredictor) => {
    const now = new Date();
    const openDate = new Date(scorePredictor.opens_at);
    return scorePredictor.state === 'draft' || openDate > now;
};

const styles = styleSheet.locals || {};

/**
 * Component for showing a ScorePredictor
 *
 * @param {object} props -
 * @param {object} props.predictionData - prediction data
 * @param {object} props.embeddedFormData form data to embed after polling submit
 * @param {HTMLElement} props.container - embed container
 * @param {boolean} props.isPreview embed is a preview in admin UI
 * @param {boolean} props.colourOverrides - colour overrides
 * @returns {Element} <Prediction />
 */
function ScorePredictor({
    predictionData,
    embeddedFormData,
    container,
    isPreview = false,
    colourOverrides
}) {
    const [startEmbedAnimation, setStartEmbedAnimation] = useState(false);
    const [latestPredictionData, setLatestPredictionData] =
        useState(predictionData);

    // Track if the data capture form is visible
    const [isDataCaptureFormVisible, setIsDataCaptureFormVisible] =
        useState(false);

    const isGated = latestPredictionData?.gated;
    const authHeader = getAuthHeader();
    const interactionEventRef = useRef(null);
    const analyticsContext = getAnalyticsContext(container);

    // Initialize InteractionEvent for analytics
    useEffect(() => {
        let userId = null;

        if (isGated && authHeader) {
            // For gated products: extract userId from JWT
            userId = getUserIdFromAuthHeader(authHeader);
        } else if (!isGated) {
            // For non-gated products: generate/retrieve from localStorage
            userId = getOrCreateEncodedUUID();
        }

        const interactionEvent = new InteractionEvent({
            activationId: latestPredictionData?.prediction_id,
            userId,
            type: 'SCORE',
            isLoggedIn: !!authHeader,
            title: latestPredictionData?.text,
            context: analyticsContext
        });

        interactionEventRef.current = interactionEvent;
    }, [
        latestPredictionData?.prediction_id,
        isGated,
        authHeader,
        analyticsContext
    ]);

    const transitionStyles = {
        entered: { opacity: 1, transform: 'rotate(0deg)' }
    };

    const isDraft =
        latestPredictionData?.state === 'draft' ||
        latestPredictionData?.state === 'scheduled';

    const isOpen = latestPredictionData?.state === 'open';

    const scorePredictorScheduled = isScheduled(latestPredictionData);
    const hasHeroImg =
        latestPredictionData?.hero_image ||
        latestPredictionData?.hero_external_image_url;

    const hasSponsorImg =
        latestPredictionData?.sponsor_image_url ||
        latestPredictionData?.sponsor_external_image_url;

    /* eslint-disable no-magic-numbers */
    const largePrediction =
        latestPredictionData?.questions?.[0]?.options.length > 15 || hasHeroImg;

    const { ref } = useInView({
        triggerOnce: true,
        threshold: largePrediction ? 0.1 : 0.5,
        onChange: (inView) => {
            if (inView) {
                setStartEmbedAnimation(true);

                if (!isPreview) {
                    sendView(
                        latestPredictionData?.prediction_id,
                        isGated,
                        authHeader
                    )
                        .then(() => {
                            interactionEventRef.current?.send(
                                'PREDICTION_VIEWED'
                            );
                        })
                        .catch((error) => {
                            // Handle the error here
                            console.error('Error sending view:', error);
                        });
                }
            }
        }
    });

    // Refetch function to get latest prediction data
    const refetch = async () => {
        const updated = await getScorePredictorData(
            latestPredictionData.prediction_id,
            isGated,
            authHeader
        );
        setLatestPredictionData(updated ? { ...updated } : {}); // force new reference
        return updated;
    };

    const onCountdownEnd = () => {
        // setTimeout(async () => {
        //     const updatedPoll = await refetch();
        //     setPoll(updatedPoll);

        console.warn('COUNTDOWN ENDED, REFRESHING ScorePredictor...');
        // }, THREE_SECONDS);
    };

    useEffect(() => {
        applyCustomSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isPreview) {
            setLatestPredictionData(predictionData);
        }
    }, [isPreview, predictionData]);

    const applyCustomSettings = () => {
        const customSettingsAttr =
            container.previousElementSibling?.getAttribute('data-fek-settings');
        if (customSettingsAttr) {
            const settings = JSON.parse(customSettingsAttr);

            if (settings.primaryColour) {
                container.style.setProperty(
                    '--fek-primary',
                    settings.primaryColour
                );
            }

            if (settings.primaryColourRgb) {
                container.style.setProperty(
                    '--fek-primary-rgb',
                    settings.primaryColourRgb
                );
            }
        } else if (colourOverrides?.hex && colourOverrides?.rgb) {
            container?.style?.setProperty('--fek-primary', colourOverrides.hex);

            container?.style?.setProperty(
                '--fek-primary-rgb',
                colourOverrides.rgb
            );
        }
    };

    return (
        <>
            <style>{styleSheet.toString()}</style>
            <Transition nodeRef={ref} in={startEmbedAnimation}>
                {(transitionState) => (
                    <>
                        <div
                            data-cy="prediction-root"
                            part="score-predictor-root"
                            ref={ref}
                            className={classNames(styles.scorePredictor, {
                                [styles.scorePredictorDraft]: isDraft,
                                [styles.scorePredictorDataCaptureActive]:
                                    isDataCaptureFormVisible
                            })}
                            style={{
                                maxWidth: predictionData.settings?.width || '',
                                ...transitionStyles[transitionState]
                            }}
                        >
                            {/* Always the first element in the component */}
                            <ScorePredictorHeader
                                scorePredictor={latestPredictionData}
                                isFormVisible={isDataCaptureFormVisible}
                                interactionEvent={interactionEventRef.current}
                            />

                            <ScorePredictorContent
                                scorePredictor={latestPredictionData}
                                refetch={refetch}
                                embeddedFormData={embeddedFormData}
                                onDataCaptureFormVisibilityChange={
                                    setIsDataCaptureFormVisible
                                }
                                isPreview={isPreview}
                                interactionEvent={interactionEventRef.current}
                                analyticsContext={analyticsContext}
                            />

                            {(isDraft || isOpen) && (
                                <ScheduledScreen
                                    scorePredictor={latestPredictionData}
                                    scorePredictorScheduled={
                                        scorePredictorScheduled
                                    }
                                    onCountdownEnd={onCountdownEnd}
                                />
                            )}

                            {hasSponsorImg && (
                                <Sponsor
                                    scorePredictor={latestPredictionData}
                                />
                            )}
                        </div>
                    </>
                )}
            </Transition>
        </>
    );
}

ScorePredictor.prototype = {
    predictionData: PropTypes.object.isRequired,
    embeddedFormData: PropTypes.object,
    container: PropTypes.node.isRequired,
    isPreview: PropTypes.bool
};

export default ScorePredictor;
